import React, { useEffect } from "react";
import {
    AddProductToBasketResponse,
    insertProductToBasket,
} from "../basketHooks";
import { assert } from "../../../../js/common/assert";
import ProductInsertedToBasketModal from "./ProductInsertedToBasketModal";
import AlreadyInBasketModal from "./AlreadyInBasketModal";
import ProductSoldOutModal from "./ProductSoldOutModal";
import DiscountCodeRemovedModal from "./DiscountCodeRemovedModal";
import TooManyPackagesOfWeekModal from "./TooManyPackagesOfWeekModal";

interface InsertBasketModalProperties {
    readonly productId: number;
    readonly onAfterClose: () => void;
}

export default function InsertBasketModal({ productId, onAfterClose }: InsertBasketModalProperties): React.JSX.Element | undefined {
    const [insertResponse, setInsertResponse] = React.useState<AddProductToBasketResponse | undefined>(undefined);
    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    const showModal = (): void => {
        setModalIsOpen(true);
    };

    const closeModal = (): void => {
        setModalIsOpen(false);
        onAfterClose();
    };

    useEffect(() => {
        const runInsert = async (): Promise<void> => {
            const response = await insertProductToBasket(productId);

            setInsertResponse(response);
            showModal();
        };

        void runInsert();
    }, [productId]);

    if (!insertResponse) {
        return undefined;
    }

    if (insertResponse.result === "success") {
        assert(insertResponse.product !== undefined);

        return <ProductInsertedToBasketModal
            basketPageUrl={insertResponse.basketPageUrl}
            modalIsOpen={modalIsOpen}
            closeModal={closeModal}
            product={insertResponse.product}
            onAfterClose={onAfterClose}
        />;
    }

    if (insertResponse.result === "already_in_basket") {
        return <AlreadyInBasketModal
            basketPageUrl={insertResponse.basketPageUrl}
            modalIsOpen={modalIsOpen}
            closeModal={closeModal}
            onAfterClose={onAfterClose}
        />;
    }

    if (insertResponse.result === "sold_out") {
        return <ProductSoldOutModal
            modalIsOpen={modalIsOpen}
            closeModal={closeModal}
            onAfterClose={onAfterClose}
        />;
    }

    if (insertResponse.result === "discount_code_already_used") {
        return <DiscountCodeRemovedModal
            modalIsOpen={modalIsOpen}
            closeModal={closeModal}
            onAfterClose={onAfterClose}
        />;
    }

    assert(insertResponse.maximalQuantity !== undefined, "Maximal quantity is undefined");

    return <TooManyPackagesOfWeekModal
        maximalQuantity={insertResponse.maximalQuantity}
        modalIsOpen={modalIsOpen}
        closeModal={closeModal}
        onAfterClose={onAfterClose}
    />;
}
